import { ArAgingBucket } from "@common/types/reports/ar-aging";
import { SchedulesRollforwardCell } from "@common/schemas/domains/accounting/schedules-report.schema";

export type PreviewRowKind = "section" | "line" | "total" | "spacer";

export interface PreviewStatementRow {
    id: string;
    label: string;
    kind: PreviewRowKind;
    amount?: number;
    hasTopBorder?: boolean;
    hasActivity?: boolean;
}

export interface PreviewStatementDatasetSource {
    shape: "statement";
    accountHeader: string;
    percentHeader: string;
    percentBase: number;
    rows: PreviewStatementRow[];
}

export interface PreviewStatementDataset extends PreviewStatementDatasetSource {
    amountHeader: string;
}

interface PreviewTrialBalanceRowBase {
    id: string;
    label: string;
    hasTopBorder?: boolean;
    hasActivity?: boolean;
}

export interface PreviewTrialBalanceAmountRow extends PreviewTrialBalanceRowBase {
    kind: "line" | "total";
    debit: number;
    credit: number;
}

export interface PreviewTrialBalanceLabelRow extends PreviewTrialBalanceRowBase {
    kind: "section" | "spacer";
}

export type PreviewTrialBalanceRow =
    | PreviewTrialBalanceAmountRow
    | PreviewTrialBalanceLabelRow;

export const hasTrialBalanceAmounts = (
    row: PreviewTrialBalanceRow,
): row is PreviewTrialBalanceAmountRow =>
    row.kind === "line" || row.kind === "total";

export interface PreviewTrialBalanceDataset {
    shape: "trial-balance";
    accountHeader: string;
    debitHeader: string;
    creditHeader: string;
    percentHeader: string;
    percentBase: number;
    rows: PreviewTrialBalanceRow[];
}

type PreviewLedgerRowKind = "account" | "transaction" | "total" | "spacer";

export interface PreviewLedgerRow {
    id: string;
    kind: PreviewLedgerRowKind;
    label?: string;
    date?: string;
    debit?: number;
    credit?: number;
    balance?: number;
    hasTopBorder?: boolean;
    isNeverUsed?: boolean;
}

export interface PreviewLedgerDataset {
    shape: "ledger";
    dateHeader: string;
    descriptionHeader: string;
    debitHeader: string;
    creditHeader: string;
    balanceHeader: string;
    percentHeader: string;
    percentBase: number;
    rows: PreviewLedgerRow[];
}

export interface PreviewSchedulesRow {
    id: string;
    label: string;
    kind: Extract<PreviewRowKind, "line" | "total">;
    depth: 0 | 1 | 2;
    cells: SchedulesRollforwardCell;
    hasTopBorder?: boolean;
}

export interface PreviewSchedulesDataset {
    shape: "schedules";
    scheduleHeader: string;
    percentHeader: string;
    percentBase: number;
    rows: PreviewSchedulesRow[];
}

export interface PreviewArAgingSummaryRow {
    id: string;
    label: string;
    kind: Extract<PreviewRowKind, "line" | "total">;
    buckets: Record<ArAgingBucket, number>;
    total: number;
    hasTopBorder?: boolean;
}

export interface PreviewArAgingSummaryDataset {
    shape: "ar-aging-summary";
    customerHeader: string;
    totalHeader: string;
    percentHeader: string;
    percentBase: number;
    rows: PreviewArAgingSummaryRow[];
}

interface PreviewArAgingDetailRowBase {
    id: string;
    label: string;
    hasTopBorder?: boolean;
}

interface PreviewArAgingDetailAmounts {
    amount: number;
    open: number;
}

export interface PreviewArAgingDetailSectionRow extends PreviewArAgingDetailRowBase {
    kind: "section";
    bucket: ArAgingBucket;
}

export interface PreviewArAgingDetailInvoiceRow
    extends PreviewArAgingDetailRowBase, PreviewArAgingDetailAmounts {
    kind: "line";
    bucket: ArAgingBucket;
    dateOffset: number;
    dueDateOffset: number;
}

export interface PreviewArAgingDetailSubtotalRow
    extends PreviewArAgingDetailRowBase, PreviewArAgingDetailAmounts {
    kind: "subtotal";
    bucket: ArAgingBucket;
}

export interface PreviewArAgingDetailTotalRow
    extends PreviewArAgingDetailRowBase, PreviewArAgingDetailAmounts {
    kind: "total";
}

export type PreviewArAgingDetailRow =
    | PreviewArAgingDetailSectionRow
    | PreviewArAgingDetailInvoiceRow
    | PreviewArAgingDetailSubtotalRow
    | PreviewArAgingDetailTotalRow;

export interface PreviewArAgingDetailDataset {
    shape: "ar-aging-detail";
    dateHeader: string;
    customerHeader: string;
    dueDateHeader: string;
    amountHeader: string;
    openHeader: string;
    percentHeader: string;
    percentBase: number;
    rows: PreviewArAgingDetailRow[];
}

export type PreviewDatasetSource =
    | PreviewStatementDatasetSource
    | PreviewTrialBalanceDataset
    | PreviewLedgerDataset
    | PreviewSchedulesDataset
    | PreviewArAgingSummaryDataset
    | PreviewArAgingDetailDataset;
